import {
  DateTimeFormatter,
  LocalDateTime,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { Typography } from '@mui/material';
import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  DateField,
  DateTimeInput,
  DeleteWithConfirmButton,
  Edit,
  Filter,
  FunctionField,
  ReferenceField,
  SaveButton,
  Show,
  ShowButton,
  SimpleForm,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  AdminEligibleEmailDetailsFragment,
  EligibleEmailAddress,
} from '../../graphql/types';
import useCanWrite from '../../hooks/useCanWrite';
import { iso8601DateTime } from '../../utils/date';

import { IDSearch } from '../reusable/IDSearch';
import AutocompleteClientInput from './components/AutocompleteClientInput';
import AutocompleteOfficeInput from './components/AutocompleteOfficeInput';
import InfiniteList from './components/InfiniteList';
import { WriteOnlyDatagrid } from './components/WriteOnlyDatagrid';
import { WriteOnlyEditButton } from './components/WriteOnlyEditButton';

const EligibleEmailAddressToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

const EligibleEmailAddressFilters: React.FC = (props) => (
  <Filter {...props}>
    <IDSearch label="Email" source="email" alwaysOn />
    <TextInput label="Last Name" source="lastName" alwaysOn />
    <TextInput label="Employee ID" source="employeeId" alwaysOn />
    <BooleanInput label="Has HPC Eligibility" source="hasHpcEligibility" />
    <AutocompleteClientInput source="clientId" label="Client" alwaysOn />
    <AutocompleteOfficeInput source="officeId" label="Office" alwaysOn />
  </Filter>
);

export const EligibleEmailAddressList: React.FC = () => (
  <InfiniteList filters={<EligibleEmailAddressFilters />}>
    <WriteOnlyDatagrid bulkActionButtons={false}>
      <TextField source="email" sortable={false} />
      <TextField source="lastName" sortable={false} />
      <TextField source="source" sortable={false} />
      <BooleanField source="isArchived" label="Archived" sortable={false} />
      <BooleanField source="isStaff" label="Staff" sortable={false} />
      <BooleanField
        source="hasHpcEligibility"
        label="Has HPC Eligibility"
        sortable={false}
      />
      <ReferenceField
        source="client.id"
        reference="Client"
        label="Client"
        sortable={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <WriteOnlyEditButton />
      <ShowButton />
    </WriteOnlyDatagrid>
  </InfiniteList>
);

const EligibleEmailAddressShowActions: React.FC = () => {
  const canWrite = useCanWrite();
  const record = useRecordContext<AdminEligibleEmailDetailsFragment>();

  const canDelete = !record?.user && record?.addedManually;

  return (
    <TopToolbar>
      <WriteOnlyEditButton />
      {record && canDelete && canWrite && (
        <DeleteWithConfirmButton
          confirmColor="warning"
          confirmTitle="Delete Eligible Email Address"
          confirmContent="Are you sure you want to delete this eligible email address?"
        />
      )}
    </TopToolbar>
  );
};

export const EligibleEmailAddressShow: React.FC = () => (
  <Show actions={<EligibleEmailAddressShowActions />}>
    <TabbedShowLayout>
      <Tab label="Basics">
        <TextField source="id" />
        <TextField source="email" />
        <TextField source="source" label="Eligibility Source" />
        <BooleanField source="isStaff" label="Staff" />
        <BooleanField source="user.registered" label="User Registered" />
        <ReferenceField source="user.id" reference="User" label="User">
          <FunctionField
            render={(record: Record<string, any>) =>
              record &&
              `${record.firstName} ${record.lastName} <${record.email}>`
            }
          />
        </ReferenceField>
        <BooleanField source="isArchived" label="Archived" />
        <FunctionField
          label="FCM Eligible"
          render={(record: EligibleEmailAddress) => {
            if (record.office) {
              return record.office.isFcmEligible
                ? 'Yes, via office'
                : 'No, via office';
            } else {
              return record.isFcmEligible ? 'Yes' : 'No';
            }
          }}
        />
        <FunctionField
          label="App Eligible"
          render={(record: EligibleEmailAddress) => {
            if (record.office) {
              return record.office.isAppEligible
                ? 'Yes, via office'
                : 'No, via office';
            } else {
              return record.isAppEligible ? 'Yes' : 'No';
            }
          }}
        />
        <DateField source="archivedAt" />
        <ReferenceField source="client.id" reference="Client" label="Client">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="office.id" reference="Office" label="Office">
          <TextField source="clientOfficeId" />
        </ReferenceField>
        <ReferenceField
          source="user.id"
          reference="User"
          label="User"
          link="show"
          emptyText="Not registered"
        />
      </Tab>
      <Tab label="High-Performance Coaching">
        <BooleanField
          source="hasHpcEligibility"
          label="Has HPC Eligibility"
          sortable={false}
        />
        <DateField
          source="hpcEligibilityStart"
          label="Start Date (UTC)"
          showTime
        />
        <DateField source="hpcEligibilityEnd" label="End Date (UTC)" showTime />
      </Tab>
      <Tab label="Other">
        <TextField source="invitingRelationship" />
        <ReferenceField
          source="invitingUser.id"
          reference="User"
          label="Inviting User"
        >
          <TextField source="email" />
        </ReferenceField>
        <TextField source="employeeId" />
        <TextField source="employeeType" />
        <TextField source="lastName" />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

const StaffInput: React.FC = () => (
  <BooleanInput
    source="isStaff"
    defaultValue
    helperText="If this is unchecked, the user can be archived the next time an eligibility file is uploaded"
  />
);

/**
 * See https://github.com/marmelab/react-admin/issues/10375
 * and https://github.com/marmelab/react-admin/blob/a70b0dfe99c5a5e6c3a4c610916060752c64704e/packages/ra-ui-materialui/src/input/DateTimeInput.tsx#L207
 *
 * This code is to convert from db UTC timestamps to something <input type="datetime-local">
 * without RA calling new Date() and applying the browser's timezone.
 */
const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/; // yyyy-MM-ddThh:mm
const formatter = DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm");

const HpcInputs: React.FC = (props) => {
  const record = useRecordContext(props);
  const [showDates, setShowDates] = React.useState(record?.hasHpcEligibility);

  return (
    <>
      <BooleanInput
        source="hasHpcEligibility"
        label="Has High-Performance Coaching Eligibility"
        onChange={(e) => setShowDates(e.target.checked)}
      />
      {showDates && (
        <>
          <Typography style={{ marginBottom: 16 }}>
            User-level access to high-performance coaching is based on the
            specified start and end dates.
          </Typography>
          <DateTimeInput
            source="hpcEligibilityStart"
            label="Start Date (UTC)"
            defaultValue={iso8601DateTime(
              ZonedDateTime.now().withFixedOffsetZone(),
            )}
            format={(v: string) => {
              if (!v) {
                return null;
              }
              if (dateTimeRegex.test(v)) {
                return v;
              }
              return ZonedDateTime.parse(v).format(formatter);
            }}
            parse={(v: string) =>
              v ? LocalDateTime.parse(v).atZone(ZoneId.UTC).toString() : null
            }
            required
          />
          <DateTimeInput
            source="hpcEligibilityEnd"
            label="End Date (UTC)"
            defaultValue={iso8601DateTime(
              ZonedDateTime.now().plusMonths(3).withFixedOffsetZone(),
            )}
            format={(v: string) => {
              if (!v) {
                return null;
              }
              if (dateTimeRegex.test(v)) {
                return v;
              }
              return ZonedDateTime.parse(v).format(formatter);
            }}
            parse={(v: string) =>
              v ? LocalDateTime.parse(v).atZone(ZoneId.UTC).toString() : null
            }
            required
          />
        </>
      )}
    </>
  );
};

const AutocompleteOfficeInputWithRecord: React.FC<{
  source: string;
  label: string;
  isRequired?: boolean;
}> = ({ source, label, isRequired }) => {
  const record = useRecordContext<AdminEligibleEmailDetailsFragment>();

  return (
    <AutocompleteOfficeInput
      source={source}
      label={label}
      clientId={record?.client?.id}
      isRequired={isRequired}
      disabled={record?.addedManually !== true} // Only allow editing if added manually
    />
  );
};

export const EligibleEmailAddressEdit: React.FC = () => (
  <Edit mutationMode="pessimistic" redirect={false}>
    <SimpleForm toolbar={<EligibleEmailAddressToolbar />}>
      <TextInput source="id" InputProps={{ disabled: true }} />
      <TextInput source="email" InputProps={{ disabled: true }} />
      <TextInput
        source="source"
        InputProps={{ disabled: true }}
        helperText="Eligibility sources include file, mbo, admin, or invitation."
      />
      <div style={{ marginTop: 16 }}>
        <AutocompleteOfficeInputWithRecord
          source="office.id"
          label="Office"
          isRequired={false}
        />
      </div>
      <BooleanInput source="isArchived" />
      <StaffInput />
      <HpcInputs />
    </SimpleForm>
  </Edit>
);

const OfficeInputWithClientFilter: React.FC = () => {
  const clientId = useWatch({ name: 'client.id' });
  const { setValue } = useFormContext();
  const prevClientIdRef = React.useRef(clientId);

  React.useEffect(() => {
    // Clear office when client changes
    if (prevClientIdRef.current !== clientId) {
      setValue('officeId', null);
      prevClientIdRef.current = clientId;
    }
  }, [clientId, setValue]);

  return (
    <AutocompleteOfficeInput
      source="officeId"
      label="Office"
      clientId={clientId}
      isRequired={false}
    />
  );
};

export const EligibleEmailAddressCreate: React.FC = () => (
  <Create>
    <SimpleForm>
      <TextInput source="email" />
      <AutocompleteClientInput source="client.id" label="Client" />
      <BooleanInput source="isArchived" />
      <OfficeInputWithClientFilter />
      <StaffInput />
      <HpcInputs />
    </SimpleForm>
  </Create>
);
